import algoliasearch from 'algoliasearch'
import { uniq, cloneDeep } from 'lodash-es'
import { getProfiles } from './profile'
import { batch1155Balances } from './ethereum'
import { toTokenId } from './rarible'
import { getBlocklist } from './augmintables'
import { getPlayerDetails, getPlayerHeadshot } from './nfldata'
import { ALGOLIA, ALGOLIA_COLLECTION, CHAIN_ID, RARIBLE_COLLECTION_ADDR } from '~/utils/constants'
import { lowercaseObjectID, toShortAddress } from '~/utils/helpers'
import { isSameAddress } from '~/utils/validation'
import store from '~/store'
import filterDefinitions from '~/data/exploreFilters.json'

const [APP, KEY] = ALGOLIA.split(':')
const client = algoliasearch(APP, KEY)
const index = client.initIndex(ALGOLIA_COLLECTION)
const nflIndex = client.initIndex('nfl_players')
const cfbIndex = client.initIndex('cfb_players')
const sortRecent = client.initIndex(ALGOLIA_COLLECTION + '_sort-recent')
const sortUpdated = client.initIndex(ALGOLIA_COLLECTION + '_sort-updated')
const sortPriceAscending = client.initIndex(ALGOLIA_COLLECTION + '_sort-price-ascending')
const sortPriceDescending = client.initIndex(ALGOLIA_COLLECTION + '_sort-price-descending')
const suggestions = client.initIndex(ALGOLIA_COLLECTION + '_suggestions')
const PAGE_SIZE = 27

export default index

let blocklist
async function itemDecorator(augmintables) {
  if (!blocklist) blocklist = await getBlocklist()

  const artistInfo = await getProfiles(uniq(augmintables.map(({ artist }) => artist)))

  let balances
  if (store.state.walletAddress) {
    balances = await batch1155Balances(
      RARIBLE_COLLECTION_ADDR,
      augmintables.map(({ objectID }) => toTokenId(objectID)),
      store.state.walletAddress
    ).catch(console.error)
  }
  return augmintables
    .filter(
      ({ artist, objectID }) =>
        !blocklist?.profiles.includes(artist?.toLowerCase()) &&
        !blocklist?.augmintables.includes(objectID)
    )
    .map((augmintable, i) => ({
      ...augmintable,
      amountOwned: balances ? balances[i] : 0,
      editions: parseInt(augmintable.editions),
      royalties: parseInt(augmintable.royalties),
      contentSize: parseInt(augmintable.contentSize),
      mintedAt: new Date(augmintable.mintedAt).toLocaleDateString(),
      username:
        artistInfo.find(({ address }) => isSameAddress(address, augmintable.artist))
          ?.name || toShortAddress(augmintable.artist),
    }))
}

export const defaultFilters = () => ({
  ...cloneDeep(filterDefinitions),
})

export async function searchItems({ algoliaOptions = {}, page = 0, filters = {} }) {
  const { search, tags, formats, sortType, status } = Object.assign(defaultFilters(), filters)

  let searchIndex
  if (sortType === 'price-ascending') searchIndex = sortPriceAscending
  else if (sortType === 'price-descending') searchIndex = sortPriceDescending
  else if (sortType === 'updated') searchIndex = sortUpdated
  else searchIndex = sortRecent

  const mimetypes = Object.keys(formats).reduce((acc, val) => {
    if (formats[val]) {
      let _type
      if ('jpg,jpeg'.includes(val)) {
        acc.push('image/jpeg')
      } else {
        if ('png,gif'.includes(val)) _type = `image/${val}`
        if (val === 'svg') _type = `image/svg+xml`
        if (val === 'mp4') _type = `video/${val}`
        if (val === 'mov') _type = `video/${val}`
        acc.push(_type)
      }
    }
    return acc
  }, [])

  const algoliaFilters = [
    `chainId:${CHAIN_ID}`,
  ]

  if (algoliaOptions.filters) algoliaFilters.push(algoliaOptions.filters)

  const algoliaTags = tags.map((tag) => `tags:"${tag}"`)
  if (algoliaTags.length) algoliaFilters.push(algoliaTags.join(' AND '))

  if (mimetypes.length) algoliaFilters.push(mimetypes.map((t) => `contentType:"${t}"`).join(' OR '))
  if (status.buyNow || sortType.includes('price')) algoliaFilters.push('buyPrice >= 0')

  // TODO: Add collection?
  // const searchOptions = { filters : `collection:${RARIBLE_COLLECTION_ADDR}`}
  if (algoliaFilters.length) algoliaOptions.filters = algoliaFilters.map((filter) => `(${filter})`).join(' AND ')

  const { hits, nbPages } = await searchIndex.search(search, {
    hitsPerPage: PAGE_SIZE,
    ...algoliaOptions,
    page,
  })
  const nextPage = page + 1 < nbPages ? page + 1 : null // page is 0-based
  return {
    items: await itemDecorator(hits),
    nextPage,
  }
}

export function getCreatorItems(walletAddress, page = 0, search = '') {
  return searchItems({
    algoliaOptions: {
      filters: `artist:${walletAddress.toLowerCase()}`,
    },
    filters: {
      search,
      sortType: 'recent',
    },
    page,
  })
}

export function getOwnerItems(ownerAddress, page = 0, search = '') {
  return searchItems({
    algoliaOptions: {
      filters: `owners:${ownerAddress.toLowerCase()}`,
    },
    filters: {
      search,
      sortType: 'updated',
    },
    page,
  })
}

export function getSellerListings(sellerAddress, page = 0, search = '') {
  return searchItems({
    algoliaOptions: {
      filters: `sellers:${sellerAddress.toLowerCase()}`,
    },
    filters: {
      search,
      sortType: 'updated',
    },
    page,
  })
}

export async function getItem(itemId) {
  const details = await index.getObject(lowercaseObjectID(itemId))
  return (await itemDecorator([details]))[0]
}

export async function getItems(itemIds) {
  if (!itemIds?.length) return []
  const { results } = await index.getObjects(itemIds.map(lowercaseObjectID))
  const missingResults = itemIds.filter((_, idx) => !results[idx])
  if (missingResults.length) console.info('Missing data for itemIds', missingResults)
  return itemDecorator(results.filter((x) => x))
}

export async function searchNFL(query) {
  const { hits } = await nflIndex.search(query, {
    hitsPerPage: 10,
  })

  return Promise.all(hits.map(async hit => {
    if (hit.PlayerID) {
      hit.PhotoUrl = await getPlayerHeadshot(hit.PlayerID) || hit.PhotoUrl
    }
    return hit
  }))
}

export async function searchCFB(query) {
  const { hits } = await cfbIndex.search(query, {
    hitsPerPage: 10,
  })
  return hits
}

export function getNFLProfileImages(playerIds) {
  if (!playerIds?.length) return []
  // We might want to get back to this later and move Headshots api to Algolia for now use Headshots directly
  return Promise.all(playerIds.map(
    async id => [id, (await getPlayerDetails(id, false)).PhotoUrl]
  )).then(urls => urls.reduce((acc, [id, url]) => {
    acc[id] = url
    return acc
  }, {}))

  // const { results } = await nflIndex.getObjects(playerIds.map((x) => x.toString()), {
  //   attributesToRetrieve: ['objectID', 'PhotoUrl'],
  // })
  // return results.reduce((acc, result) => {
  //   if (result) acc[result.objectID] = result.PhotoUrl
  //   return acc
  // }, {})
}

export async function getSuggestions(search) {
  const res = await suggestions.search(search, {
    hitsPerPage: 10,
    attributesToRetrieve: ['objectID'],
  })
  return res.hits.map(({ objectID }) => objectID)
}

export function clearCache() {
  console.info('Clearing algolia cache')
  return client.clearCache()
}
